import React from "react";
import { useSelector } from "react-redux";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { getFormattedAmount, getFormattedPercentage } from "Shared/Utils";
import { PerformanceTag } from "Shared/PerformanceTag";
import { Tokens } from "Shared/Tokens";

const computeFallbackTradeStats = (asset = {}) => {
    const txs = asset.transactions || [];
    let totalBought = 0;
    let totalBoughtCoins = 0;
    let totalSold = 0;
    let totalSoldCoins = 0;

    txs.forEach((tx) => {
        const snap = tx?.portfolioSnapshot || tx || {};
        const prev = snap.previous || tx?.previous || {};
        const next = snap.latest || tx?.latest || {};
        const prevCoins = Number(prev.numberOfCoins || 0);
        const nextCoins = Number(next.numberOfCoins || 0);
        const coinDelta = Math.abs(nextCoins - prevCoins);
        const price = Number(
            snap.transactionMarketPrice ?? tx?.transactionMarketPrice ?? 0
        );
        const invDelta = Math.abs(
            Number(next.investment || 0) - Number(prev.investment || 0)
        );
        const txValue = coinDelta * price > 0 ? coinDelta * price : invDelta;

        const typeLower = String(tx?.type || "").toLowerCase();
        const isBuy =
            typeLower === "buy" ||
            (typeLower !== "sell" && nextCoins >= prevCoins);

        if (isBuy) {
            totalBought += txValue;
            totalBoughtCoins += coinDelta;
        } else {
            totalSold += txValue;
            totalSoldCoins += coinDelta;
        }
    });

    const totalVolume = totalBought + totalSold;
    const netFlow = totalBought - totalSold;
    const isExited =
        Number(asset.portfolio?.latest?.numberOfCoins || 0) <= 1e-10 &&
        txs.length > 0;
    const realizedPL =
        asset.realizedPL !== undefined
            ? Number(asset.realizedPL)
            : totalSold - totalBought;
    const avgExitPrice = totalSoldCoins > 0 ? totalSold / totalSoldCoins : 0;

    return {
        totalVolume,
        totalBought,
        totalBoughtCoins,
        totalSold,
        totalSoldCoins,
        netFlow,
        realizedPL,
        isExited,
        avgExitPrice,
    };
};

const ViewPortfolio = (props) => {
    const {
        coinInfo = null,
        coinGeckoInfo = null,
        tradeStats: propTradeStats,
        currency,
    } = props;
    const asset = useSelector((state) => state.assets?.selectedAsset || {});
    const settings = useSelector((state) => state.settings || {});
    const displayCurrency = currency || settings?.currency || "INR";
    const symbol = getCurrencySymbol(displayCurrency);

    const marketInfo = coinGeckoInfo || coinInfo || {};
    const { portfolio = {} } = asset;

    const stats = propTradeStats || computeFallbackTradeStats(asset);
    const numberOfCoins = Number(portfolio.latest?.numberOfCoins || 0);
    const currentPrice = Number(marketInfo.current_price || 0);
    const avgBuyPrice = Number(portfolio.latest?.marketPrice || 0);
    const investedAmount = Number(portfolio.latest?.investment || 0);
    const currentPortfolioValue = numberOfCoins * currentPrice;
    const unrealizedPL = currentPortfolioValue - investedAmount;
    const roiPct =
        investedAmount > 0 ? (unrealizedPL / investedAmount) * 100 : 0;
    const priceChange24h = Number(marketInfo.price_change_percentage_24h || 0);

    const isExited = Boolean(stats.isExited);
    const totalBought = Number(stats.totalBought || 0);
    const totalSold = Number(stats.totalSold || 0);
    const realizedPL = Number(stats.realizedPL || 0);
    const realizedRoiPct =
        totalBought > 0 ? (realizedPL / totalBought) * 100 : 0;

    const profitColor = Tokens.profit || Tokens.positive;
    const lossColor = Tokens.loss || Tokens.negative;

    let unrealizedColor = Tokens.textSecondary;
    if (unrealizedPL > 0) unrealizedColor = profitColor;
    if (unrealizedPL < 0) unrealizedColor = lossColor;

    let realizedColor = Tokens.textSecondary;
    if (realizedPL > 0) realizedColor = profitColor;
    if (realizedPL < 0) realizedColor = lossColor;

    let change24hColor = Tokens.textSecondary;
    if (priceChange24h > 0) change24hColor = profitColor;
    if (priceChange24h < 0) change24hColor = lossColor;

    const metrics = [
        {
            label: "Holdings",
            value: `${getFormattedAmount(numberOfCoins, true, 4)}${
                asset.symbol ? ` ${asset.symbol.toUpperCase()}` : ""
            }`,
            color: Tokens.textPrimary,
        },
        {
            label: "Average buy price",
            value: `${symbol}${getFormattedAmount(avgBuyPrice, true, 2)}`,
            color: Tokens.textPrimary,
        },
        {
            label: "Current price",
            value: `${symbol}${getFormattedAmount(currentPrice, true, 2)}`,
            color: Tokens.textPrimary,
        },
        isExited
            ? {
                  label: "Total bought",
                  value: `${symbol}${getFormattedAmount(totalBought, true, 2)}`,
                  color: Tokens.textPrimary,
              }
            : {
                  label: "Invested amount",
                  value: `${symbol}${getFormattedAmount(
                      investedAmount,
                      true,
                      2
                  )}`,
                  color: Tokens.textPrimary,
              },
        isExited
            ? {
                  label: "Total sold",
                  value: `${symbol}${getFormattedAmount(totalSold, true, 2)}`,
                  color: Tokens.textPrimary,
              }
            : {
                  label: "Net return",
                  value: getFormattedPercentage(roiPct, 2),
                  color: unrealizedColor,
              },
        isExited
            ? {
                  label: "Realized P&L",
                  value: `${
                      realizedPL >= 0 ? "+" : "-"
                  }${symbol}${getFormattedAmount(
                      Math.abs(realizedPL),
                      true,
                      2
                  )}`,
                  color: realizedColor,
              }
            : {
                  label: "24h price change",
                  value: getFormattedPercentage(priceChange24h, 2),
                  color: change24hColor,
              },
    ];

    return (
        <div
            style={{
                backgroundColor: Tokens.card,
                border: `1px solid ${Tokens.hairline}`,
                borderRadius: Tokens.radiusCard,
                padding: "18px 20px",
                marginBottom: "16px",
            }}
        >
            <div
                style={{
                    color: Tokens.textSecondary,
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: "12px",
                }}
            >
                Position overview
            </div>

            <div
                style={{
                    paddingBottom: "16px",
                    marginBottom: "16px",
                    borderBottom: `1px solid ${Tokens.hairline}`,
                }}
            >
                {isExited ? (
                    <div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                                marginBottom: "4px",
                            }}
                        >
                            Closed position · Realized P&amp;L
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap",
                            }}
                        >
                            <span
                                style={{
                                    color:
                                        realizedPL >= 0
                                            ? profitColor
                                            : lossColor,
                                    fontSize: "28px",
                                    fontWeight: 600,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    lineHeight: 1.2,
                                }}
                            >
                                {realizedPL >= 0 ? "+" : "-"}
                                {symbol}
                                {getFormattedAmount(
                                    Math.abs(realizedPL),
                                    true,
                                    2
                                )}
                            </span>
                            {PerformanceTag(realizedRoiPct, true)}
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                            flexWrap: "wrap",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                    marginBottom: "4px",
                                }}
                            >
                                Position value
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    flexWrap: "wrap",
                                }}
                            >
                                <span
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontSize: "28px",
                                        fontWeight: 600,
                                        fontFamily: Tokens.fontMono,
                                        fontVariantNumeric: "tabular-nums",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {symbol}
                                    {getFormattedAmount(
                                        currentPortfolioValue,
                                        true,
                                        2
                                    )}
                                </span>
                                {PerformanceTag(roiPct, true)}
                            </div>
                        </div>

                        <div style={{ textAlign: "right" }}>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                    marginBottom: "4px",
                                }}
                            >
                                Unrealized P&amp;L
                            </div>
                            <div
                                style={{
                                    color: unrealizedColor,
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {unrealizedPL >= 0 ? "+" : "-"}
                                {symbol}
                                {getFormattedAmount(
                                    Math.abs(unrealizedPL),
                                    true,
                                    2
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "16px",
                }}
            >
                {metrics.map((metric) => (
                    <div key={metric.label} style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                                marginBottom: "4px",
                            }}
                        >
                            {metric.label}
                        </div>
                        <div
                            style={{
                                color: metric.color,
                                fontSize: "15px",
                                fontWeight: 500,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {metric.value}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ViewPortfolio;
